import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import UniversalHeader from '@/components/shared/UniversalHeader';
import UniversalFooter from '@/components/shared/UniversalFooter';
import { MoveRight, Shield, Heart, Lock } from 'lucide-react';

export default function AboutPage() {
    return (
        <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-[#D64463] selection:text-white overflow-x-hidden flex flex-col">
            <UniversalHeader className="bg-[#0F172A] border-transparent text-white" />

            {/* Hero Section - Premium & Impactful */}
            <section className="relative pt-32 pb-24 lg:pt-48 lg:pb-32 overflow-hidden bg-[#FDF8F6]">

                {/* Background Elements */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-rose-50/50" />
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-4xl mx-auto text-center space-y-8 animate-fade-in-up">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 shadow-sm mb-4">
                            <span className="w-2 h-2 rounded-full bg-[#D64463] animate-pulse" />
                            <span className="text-xs font-bold text-slate-600 uppercase tracking-widest">Our Story</span>
                        </div>

                        <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 mb-6 leading-[1.1]">
                            Healthcare without <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D64463] to-[#9C27B0]">
                                borders or barriers.
                            </span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
                            We're building the digital infrastructure to make your health history accessible, secure, and portable.
                        </p>
                    </div>
                </div>
            </section>

            {/* Values / Principles Section */}
            <section className="py-24 bg-white relative">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16 max-w-2xl mx-auto">
                        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Built on Trust & Transparency</h2>
                        <p className="text-slate-500 text-lg">Our core principles guide every decision we make at CareVault.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {/* Value 1 */}
                        <div className="group p-8 rounded-3xl bg-slate-50 border border-slate-100 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 hover:-translate-y-1">
                            <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Shield className="h-7 w-7 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-3">Patient Ownership</h3>
                            <p className="text-slate-600 leading-relaxed">
                                We believe you should own your health data, not hospitals. You decide who sees it and when.
                            </p>
                        </div>

                        {/* Value 2 */}
                        <div className="group p-8 rounded-3xl bg-slate-50 border border-slate-100 hover:shadow-xl hover:shadow-rose-500/10 transition-all duration-300 hover:-translate-y-1">
                            <div className="w-14 h-14 rounded-2xl bg-rose-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Heart className="h-7 w-7 text-[#D64463]" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-3">Health Accessibility</h3>
                            <p className="text-slate-600 leading-relaxed">
                                Quality care shouldn't depend on where you live. We make your records travel with you.
                            </p>
                        </div>

                        {/* Value 3 */}
                        <div className="group p-8 rounded-3xl bg-slate-50 border border-slate-100 hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 hover:-translate-y-1">
                            <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Lock className="h-7 w-7 text-purple-600" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-3">uncompromised Privacy</h3>
                            <p className="text-slate-600 leading-relaxed">
                                Your data is encrypted at rest and in transit. even we can't see your personal records.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-[#0F172A] relative overflow-hidden text-white">
                <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-10" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D64463] rounded-full blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2" />

                <div className="container mx-auto px-4 relative z-10 text-center">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to take control?</h2>
                    <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
                        Join thousands of users who are securing their health future today.
                    </p>
                    <Button asChild size="lg" className="h-14 px-8 rounded-full bg-white text-slate-900 font-bold text-lg hover:bg-slate-100 hover:scale-105 transition-all shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)]">
                        <Link to="/register">
                            Get Started Now <MoveRight className="ml-2 h-5 w-5" />
                        </Link>
                    </Button>
                </div>
            </section>

            <UniversalFooter />
        </div>
    );
}
